/**
 * One firing of a detection-automation rule: what triggered it, what was
 * ignited, the simulation it started and the GIS fires it published. Used
 * on the incident page and in Project Settings -> Automatic simulations.
 */
import { Box, Chip, Tooltip, Typography } from '@mui/material';

import { AutomationRun, AutomationRunStatus, AutomationTrigger } from '../../api/automationRules';
import { formatWhen } from '../../utils/incidentRekindle';
import { realtimeKindLabel } from '../../utils/realtimeKindLabels';

const STATUS_LABEL: Record<AutomationRunStatus, string> = {
  QUEUED: 'Queued',
  SIMULATING: 'Simulating',
  PUBLISHING: 'Publishing to GIS',
  DONE: 'Done',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
};

const STATUS_COLOR: Record<
  AutomationRunStatus,
  'default' | 'info' | 'success' | 'error' | 'warning'
> = {
  QUEUED: 'default',
  SIMULATING: 'info',
  PUBLISHING: 'info',
  DONE: 'success',
  FAILED: 'error',
  SKIPPED: 'warning',
};

const TRIGGER_LABEL: Record<AutomationTrigger, string> = {
  new_incident: 'new incident',
  escalated: 'rekindle escalated',
  rekindle: 'rekindle',
  reopened: 'incident reopened',
  secondary: 'fire from a neighbouring project',
  manual: 'incident created by hand',
};

const IGNITION_LABEL: Record<string, string> = {
  points: 'detection points',
  hull: 'cluster outline',
  perimeter: 'mapped perimeter',
  none: 'nothing to ignite',
};

function ignitionSummary(run: AutomationRun): string | null {
  const { mode, count } = run.ignition ?? {};
  if (!mode || mode === 'none') return null;
  const what = IGNITION_LABEL[mode] ?? mode;
  return count && count > 1 ? `${count} ${what}` : what;
}

function gisChipColor(status: string): 'success' | 'error' | 'default' {
  if (status === 'PUBLISHED') return 'success';
  if (status === 'FAILED') return 'error';
  return 'default';
}

export default function AutomationRunRow({
  run,
  showIncident = false,
  light = false,
}: {
  run: AutomationRun;
  /** Prefix the line with the incident number (project-level lists). */
  showIncident?: boolean;
  /** Render for a dark background (the project settings modal). */
  light?: boolean;
}) {
  const ignition = ignitionSummary(run);
  const primary = light ? '#fff' : undefined;
  const secondary = light ? 'rgba(255, 255, 255, 0.6)' : 'text.secondary';
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
      <Chip
        size="small"
        color={STATUS_COLOR[run.status] ?? 'default'}
        label={STATUS_LABEL[run.status] ?? run.status}
        sx={{ mt: 0.25, minWidth: 96 }}
      />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={{ color: primary }}>
          {showIncident ? `#${run.incident_id} · ` : ''}
          {realtimeKindLabel(run.kind)} · {run.duration_hours} h
          {run.publish_to_gis ? ' · publish to GIS' : ''}
          {ignition ? ` · from ${ignition}` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: secondary, display: 'block' }}>
          {TRIGGER_LABEL[run.trigger] ?? run.trigger}
          {run.created_at ? ` · ${formatWhen(run.created_at)}` : ''}
          {run.detail ? ` — ${run.detail}` : ''}
        </Typography>
        {run.simulation && (
          <Typography variant="caption" sx={{ color: secondary, display: 'block' }}>
            Simulation “{run.simulation.name}” · {run.simulation.status.toLowerCase()}
          </Typography>
        )}
        {run.gis.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            {run.gis.map((layer) => (
              <Tooltip key={layer.id} title={layer.error ?? `GIS fire ${layer.fire}`}>
                <Chip
                  size="small"
                  variant="outlined"
                  color={gisChipColor(layer.status)}
                  label={`${layer.fire} · ${layer.status.toLowerCase()}`}
                  sx={light ? { color: '#fff', borderColor: 'rgba(255,255,255,0.3)' } : undefined}
                />
              </Tooltip>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
